import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ruler, MapPin, Search as SearchIcon } from "lucide-react";
import { FLOORING_TYPES } from "@/lib/api";
import { useUnit } from "@/context/UnitContext";
import { UnitToggle } from "@/components/UnitToggle";
import { mToUnit, unitToM, convertDisplay, unitLabel, milesToKm } from "@/lib/units";

export default function SearchForm({ compact = false, initial = {} }) {
  const nav = useNavigate();
  const { unit } = useUnit();
  // Keep ONE precise canonical metres value (wM/lM); the display strings only
  // change when the user types or switches units. Switching units never alters
  // the physical size — it just re-formats the untouched metres value.
  const [roomWidth, setRoomWidth] = useState(() => mToUnit(initial.room_width, unit));
  const [roomLength, setRoomLength] = useState(() => mToUnit(initial.room_length, unit));
  const [wM, setWM] = useState(() => (initial.room_width ? Number(initial.room_width) : null));
  const [lM, setLM] = useState(() => (initial.room_length ? Number(initial.room_length) : null));
  const [postcode, setPostcode] = useState(initial.postcode || "");
  const initMiles = initial.radius_km ? String(Math.round(Number(initial.radius_km) / 1.60934)) : "25";
  const [radius, setRadius] = useState(["3", "5", "10", "25", "50"].includes(initMiles) ? initMiles : "25");
  const [flooringType, setFlooringType] = useState(initial.flooring_type || "");
  const [maxPrice, setMaxPrice] = useState(initial.max_price || "");

  function onWidth(val) { setRoomWidth(val); setWM(unitToM(val, unit)); }
  function onLength(val) { setRoomLength(val); setLM(unitToM(val, unit)); }

  // Unit switch: re-format the display only; canonical metres (wM/lM) untouched.
  function switchUnit(from, to) {
    setRoomWidth((v) => convertDisplay(v, from, to));
    setRoomLength((v) => convertDisplay(v, from, to));
  }

  function submit(e) {
    e.preventDefault();
    const params = new URLSearchParams();
    // Round canonical metres to the nearest mm so unit round-trips can't shift
    // a dimension enough to exclude an exact-fit listing.
    const w = wM != null ? Math.round(wM * 1000) / 1000 : null;
    const l = lM != null ? Math.round(lM * 1000) / 1000 : null;
    if (w) params.set("room_width", String(w));
    if (l) params.set("room_length", String(l));
    if (postcode) params.set("postcode", postcode);
    if (radius) params.set("radius_km", String(Math.round(milesToKm(Number(radius)) * 100) / 100));
    if (flooringType) params.set("flooring_type", flooringType);
    if (maxPrice) params.set("max_price", maxPrice);
    nav(`/search?${params.toString()}`);
  }

  return (
    <form
      onSubmit={submit}
      className={`bg-white border border-slate-200 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-4 sm:p-6 ${
        compact ? "" : "space-y-5"
      }`}
      data-testid="search-form"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="col-span-2 sm:col-span-4 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Ruler className="w-3.5 h-3.5" /> Room size</span>
          <UnitToggle onSwitch={switchUnit} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
            Room width ({unitLabel(unit)})
          </label>
          <input
            type="number"
            step="any"
            min="0"
            required
            value={roomWidth}
            onChange={(e) => onWidth(e.target.value)}
            placeholder={unit === "in" ? "e.g. 138" : "e.g. 350"}
            className="w-full h-11 rounded-md border border-slate-300 px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            data-testid="input-room-width"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
            Room length ({unitLabel(unit)})
          </label>
          <input
            type="number"
            step="any"
            min="0"
            required
            value={roomLength}
            onChange={(e) => onLength(e.target.value)}
            placeholder={unit === "in" ? "e.g. 165" : "e.g. 420"}
            className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            data-testid="input-room-length"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" aria-hidden="true" /> Postcode
          </label>
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            placeholder="e.g. M4 1HW"
            className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            data-testid="input-postcode"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Within</label>
          <select
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            className="w-full h-11 rounded-md border border-slate-300 px-2 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            data-testid="select-radius"
          >
            <option value="3">3 miles</option>
            <option value="5">5 miles</option>
            <option value="10">10 miles</option>
            <option value="25">25 miles</option>
            <option value="50">50 miles</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Flooring type</label>
          <select
            value={flooringType}
            onChange={(e) => setFlooringType(e.target.value)}
            className="w-full h-11 rounded-md border border-slate-300 px-2 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            data-testid="select-flooring-type"
          >
            <option value="">Any</option>
            {FLOORING_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Max price (£)</label>
          <input
            type="number"
            min="0"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            placeholder="Any"
            className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
            data-testid="input-max-price"
          />
        </div>
        <button
          type="submit"
          className="sm:mt-6 h-11 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold px-5 flex items-center justify-center gap-2 transition-colors col-span-2 sm:col-span-1"
          data-testid="search-submit"
        >
          <SearchIcon className="w-4 h-4" aria-hidden="true" /> Find flooring
        </button>
      </div>
    </form>
  );
}
